import { Outlet, createFileRoute, linkOptions } from '@tanstack/react-router'
import { useState } from 'react'
import type {
  CoverageFilters,
  CoverageResponse,
  CoverageSortingState,
  CoverageStatus,
} from 'types'

import CoverRequestDialog from '@/components/coverage/cover-request-dialog'
import StreamingExportDialog, {
  ExportType,
} from '@/components/export/streaming-export-dialog'
import CoverageStatusTabs from '@/components/filters/coverage-status-tabs'
import SearchBar from '@/components/search-bar'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import CoveragesTable from '@/components/tables/coverages/coverages-table'
import { ViewTransitionType } from '@/css/view-transition.types'
import { DEFAULT_PAGE_SIZE } from '@/lib/table'

export const Route = createFileRoute(
  '/app/data/companies/$uuid/(mainPage)/debtor/coverages',
)({
  component: RouteComponent,
})

const rowLink = (coverage: CoverageResponse) =>
  linkOptions({
    from: Route.fullPath,
    to: '/app/data/companies/$uuid/debtor/coverages/$coverageUuid',
    params: { coverageUuid: coverage.uuid },
    viewTransition: { types: [ViewTransitionType.SheetParentToChild] },
  })

function RouteComponent() {
  const { uuid: companyUuid } = Route.useParams()
  const [filters, setFilters] = useState<CoverageFilters>({
    debtorCompanyUuid: companyUuid,
  })
  const [pagination, setPagination] = useState<CursorPaginationState>({
    cursor: undefined,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [sorting, setSorting] = useState<CoverageSortingState>([])

  const handleFiltersChange = (
    updater: (filters: CoverageFilters) => CoverageFilters,
  ) => {
    setFilters((previous) => ({
      ...updater(previous),
      debtorCompanyUuid: companyUuid,
    }))
    // reset pagination to the first page on filter change
    setPagination((previous) => ({ ...previous, cursor: undefined }))
  }

  const handleSearchChange = (search: string) =>
    handleFiltersChange((previous) => ({ ...previous, search }))

  const handleStatusChange = (
    status: CoverageStatus | undefined | 'insistence',
  ) => handleFiltersChange((previous) => ({ ...previous, status }))

  return (
    <div className="flex h-full w-full flex-col justify-between gap-4">
      <div className="flex items-center justify-between gap-2">
        <SearchBar
          tabIndex={1}
          text={filters.search}
          setText={handleSearchChange}
        />
        <StreamingExportDialog
          type={ExportType.Coverages}
          filters={filters}
          buttonVariant="outlineGray"
        />
        <CoverRequestDialog debtorCompanyUuid={companyUuid} />
      </div>
      <CoverageStatusTabs
        value={filters.status}
        onValueChange={handleStatusChange}
        filters={filters}
      />
      <CoveragesTable
        filters={filters}
        setFilters={handleFiltersChange}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        rowLink={rowLink}
      />
      <Outlet />
    </div>
  )
}
